import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, formatCZK, formatQty } from "@/components/Shared";
import { toast } from "sonner";
import { Coffee, Croissant, CakeSlice, Cookie, Cookie as CookieIcon, Pizza, Soup, X, Factory, AlertTriangle, Undo2 } from "lucide-react";
import SearchBar from "@/components/SearchBar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { packageSize, packageLabel, toPieces, formatQtyWithPackage, productUnitOptions, unitRatio } from "@/lib/units";

const ICONS = {
  coffee: Coffee,
  croissant: Croissant,
  cake: CakeSlice,
  cookie: Cookie,
  pizza: Pizza,
  soup: Soup,
};

export default function Production() {
  const [products, setProducts] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [avail, setAvail] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [matAvail, setMatAvail] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [qty, setQty] = useState("");
  const [unitMode, setUnitMode] = useState("piece");
  const [reverse, setReverse] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");

  const prodWh = warehouses.find((w) => w.type === "production");

  const load = async () => {
    const [p, r, w, a, m, ma] = await Promise.all([
      api.get("/products"),
      api.get("/recipes"),
      api.get("/warehouses"),
      api.get("/stock/product-availability"),
      api.get("/materials"),
      api.get("/stock/material-availability"),
    ]);
    setProducts(p.data);
    setRecipes(r.data);
    setWarehouses(w.data);
    setAvail(a.data);
    setMaterials(m.data);
    setMatAvail(ma.data);
  };

  useEffect(() => {
    load();
  }, []);

  const availMap = useMemo(() => {
    const m = {};
    avail.forEach((a) => {
      if (!m[a.product_id]) m[a.product_id] = { qty: 0, available: 0, pending: 0 };
      m[a.product_id].qty += a.quantity;
      m[a.product_id].available += a.available ?? a.quantity;
      m[a.product_id].pending += a.pending_dispatch ?? 0;
    });
    return m;
  }, [avail]);

  const recipeMap = useMemo(() => {
    const m = {};
    recipes.forEach((r) => (m[r.product_id] = r));
    return m;
  }, [recipes]);

  // For currently-selected product + qty, compute material shortfalls
  const matStockMap = useMemo(() => {
    const m = {};
    matAvail.forEach((x) => (m[x.material_id] = x.quantity));
    return m;
  }, [matAvail]);

  const predictedShortfalls = useMemo(() => {
    if (!selected) return [];
    const recipe = recipeMap[selected.id];
    if (!recipe) return [];
    const out_qty = Number(recipe.output_quantity || 1);
    const out_unit = recipe.output_unit || "piece";
    const outPieces = out_qty * unitRatio(selected, out_unit);
    const pieces = toPieces(selected, unitMode, qty);
    if (!pieces || !outPieces) return [];
    const factor = pieces / outPieces;
    const list = [];
    for (const ing of recipe.ingredients || []) {
      const needed = Number(ing.quantity || 0) * factor;
      const have = Number(matStockMap[ing.material_id] || 0);
      if (have + 1e-9 < needed) {
        const mat = materials.find((m) => m.id === ing.material_id);
        list.push({
          material_id: ing.material_id,
          name: mat?.name || ing.material_id,
          unit: mat?.unit || "",
          needed: Math.round(needed * 1000) / 1000,
          have: Math.round(have * 1000) / 1000,
          missing: Math.round((needed - have) * 1000) / 1000,
        });
      }
    }
    return list;
  }, [selected, qty, unitMode, recipeMap, matStockMap, materials]);

  const openTile = (product) => {
    setSelected(product);
    setQty("");
    setUnitMode(product.default_production_unit || "piece");
    setReverse(false);
    setOpen(true);
  };

  const submit = async () => {
    const count = parseFloat(qty);
    if (!count || count <= 0) {
      toast.error("Zadejte platné množství");
      return;
    }
    const pieces = toPieces(selected, unitMode, qty);
    if (!prodWh) {
      toast.error("Není vytvořen výrobní sklad");
      return;
    }
    const signedPieces = reverse ? -pieces : pieces;
    const signedCount = reverse ? -count : count;
    setSubmitting(true);
    try {
      const { data } = await api.post("/production", {
        product_id: selected.id,
        warehouse_id: prodWh.id,
        quantity: signedPieces,
        unit_mode: unitMode,
        unit_count: signedCount,
      });
      if (reverse) {
        const refunded = data.refunded_materials || [];
        const list = refunded
          .map((r) => `${r.material_name} +${formatQty(r.quantity)} ${r.unit}`)
          .join(", ");
        toast.success(
          `Storno provedeno: −${formatQty(pieces, "ks")}. Vráceno: ${list || "—"}`,
          { duration: 8000 }
        );
      } else {
        const sf = data.shortfalls || [];
        if (sf.length > 0) {
          const list = sf.map((s) => `${s.material_name} (${formatQty(s.quantity)} ${s.unit})`).join(", ");
          toast.warning(
            `Vyrobeno, ale suroviny šly do mínusu: ${list}. Doplňte na sklad.`,
            { duration: 8000 }
          );
        } else {
          toast.success(
            `Vyrobeno ${formatQty(pieces, "ks")} — ${formatCZK(data.total_cost)} (${formatCZK(data.unit_cost)}/ks)`
          );
        }
      }
      setOpen(false);
      setSelected(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="production-page">
      <PageHeader
        eyebrow="Výroba"
        title="Spustit výrobu"
        description="Klikněte na výrobek, zadejte počet kusů. Systém automaticky spotřebuje suroviny FIFO podle šarží a vytvoří novou výrobní šarži s vypočtenou výrobní cenou."
      />

      {products.length === 0 && (
        <div className="card-cafe p-8 text-center">
          <Factory className="w-8 h-8 mx-auto mb-3 text-[#57534E]" />
          <h3 className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>
            Zatím žádné výrobky
          </h3>
          <p className="text-sm text-[#57534E] mt-1">
            Přidejte výrobky a technologické postupy v Administraci.
          </p>
        </div>
      )}

      {products.length > 0 && (
        <div className="mb-4">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Hledat výrobek…"
            testid="production-search"
          />
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-5">
        {products.filter((p) => {
          if (p.deleted || !recipeMap[p.id]) return false;
          const q = search.trim().toLowerCase();
          if (!q) return true;
          return p.name.toLowerCase().includes(q);
        }).map((p) => {
          const Icon = ICONS[p.icon] || Coffee;
          const hasRecipe = true;
          const stockInfo = availMap[p.id] || { qty: 0, available: 0, pending: 0 };
          return (
            <button
              key={p.id}
              data-testid={`production-tile-${p.id}`}
              onClick={() => openTile(p)}
              className="group flex flex-col items-center justify-center p-7 bg-white border-2 rounded-2xl shadow-sm transition-all text-left border-[#E5E1D8] hover:border-[#D4A373] hover:-translate-y-1 hover:shadow-md cursor-pointer"
            >
              <div className="w-16 h-16 rounded-full bg-[#F9F6F0] grid place-items-center mb-4 group-hover:bg-[#D4A373]/25 transition-colors">
                <Icon className="w-7 h-7 text-[#3A2D27]" />
              </div>
              <div className="text-base font-medium text-[#1C1917] text-center">{p.name}</div>
              {hasRecipe && (
                <div className="mt-2 text-xs text-center">
                  <span className="text-[#376A4D] font-medium">
                    {formatQtyWithPackage(stockInfo.available, p, "ks")}
                  </span>
                  {stockInfo.pending > 0 && (
                    <span className="text-[#9E4747] block">
                      v expedici {formatQtyWithPackage(stockInfo.pending, p, "ks")}
                    </span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="production-dialog" className="sm:max-w-md bg-[#FFFFFF] border-[#E5E1D8]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>
              Výroba — {selected?.name}
            </DialogTitle>
            <DialogDescription>
              Zadejte, kolik kusů bylo vyrobeno. Suroviny se odečtou automaticky.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 pt-1">
            {/* Toggle: Výroba / Storno */}
            <div>
              <div className="text-sm font-medium mb-1.5">Režim</div>
              <div className="inline-flex rounded-md border border-[#E5E1D8] overflow-hidden">
                <button
                  data-testid="production-mode-normal"
                  type="button"
                  onClick={() => setReverse(false)}
                  className={`px-4 py-2 text-sm inline-flex items-center gap-1.5 ${
                    !reverse ? "bg-[#3A2D27] text-white" : "bg-white text-[#3A2D27]"
                  }`}
                >
                  <Factory className="w-4 h-4" /> Výroba
                </button>
                <button
                  data-testid="production-mode-reverse"
                  type="button"
                  onClick={() => setReverse(true)}
                  className={`px-4 py-2 text-sm inline-flex items-center gap-1.5 ${
                    reverse ? "bg-[#9E4747] text-white" : "bg-white text-[#3A2D27]"
                  }`}
                >
                  <Undo2 className="w-4 h-4" /> Storno
                </button>
              </div>
              {reverse && (
                <div className="text-xs text-[#9E4747] mt-1.5 leading-relaxed">
                  Odečte se z posledních vyrobených šarží (LIFO) a suroviny (vč. polotovarů)
                  se vrátí zpět na sklad.
                </div>
              )}
            </div>

            {selected && productUnitOptions(selected).length > 1 && (
              <div>
                <div className="text-sm font-medium mb-1.5">Jednotka</div>
                <div className="inline-flex flex-wrap rounded-md border border-[#E5E1D8] overflow-hidden">
                  {productUnitOptions(selected).map((o) => (
                    <button
                      key={o.value}
                      data-testid={`production-unit-${o.value}`}
                      type="button"
                      onClick={() => setUnitMode(o.value)}
                      className={`px-4 py-2 text-sm ${
                        unitMode === o.value ? "bg-[#3A2D27] text-white" : "bg-white text-[#3A2D27]"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <label className="text-sm font-medium block">
              Počet {unitMode && unitMode !== "piece" ? unitMode : "kusů"}
            </label>
            <input
              data-testid="production-qty-input"
              autoFocus
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              min="0"
              step="1"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="input-cafe w-full text-2xl"
              placeholder="např. 30"
            />
            {selected && unitMode !== "piece" && parseFloat(qty) > 0 && (
              <div className="text-xs text-[#57534E]">
                = {toPieces(selected, unitMode, qty)} ks
              </div>
            )}
            {selected && recipeMap[selected.id] && (
              <div className="text-xs text-[#57534E]">
                Postup: 1 dávka ={" "}
                {(() => {
                  const r = recipeMap[selected.id];
                  const ou = r.output_unit || "piece";
                  if (ou === "piece") return `${formatQty(r.output_quantity, "ks")}`;
                  const ratio = unitRatio(selected, ou);
                  const label = ou === "package" ? packageLabel(selected) : ou;
                  return `${formatQty(r.output_quantity)} ${label} (po ${ratio} ks)`;
                })()}
                {" · "}{recipeMap[selected.id].ingredients.length} surovin
              </div>
            )}

            {predictedShortfalls.length > 0 && (
              <div
                data-testid="production-shortfall-warning"
                className="mt-2 rounded-md border border-[#C29329]/40 bg-[#C29329]/10 p-3"
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-[#C29329] shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#8B6A1A]">
                      Pozor — některé suroviny nejsou skladem
                    </div>
                    <div className="text-xs text-[#8B6A1A] mt-0.5">
                      Výrobu lze přesto spustit, suroviny půjdou do mínusu a doplníte je při dalším příjmu.
                    </div>
                    <ul className="mt-2 space-y-0.5 text-xs">
                      {predictedShortfalls.map((s) => (
                        <li key={s.material_id} className="flex justify-between gap-3">
                          <span className="text-[#1C1917]">{s.name}</span>
                          <span className="mono text-[#9E4747]">
                            chybí {formatQty(s.missing)} {s.unit}
                            <span className="text-[#57534E]">
                              {" "}(skladem {formatQty(s.have)} / třeba {formatQty(s.needed)})
                            </span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-4 py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0]"
            >
              Zrušit
            </button>
            <button
              data-testid="production-confirm"
              onClick={submit}
              disabled={submitting}
              className={reverse ? "px-4 py-2 rounded-md bg-[#9E4747] text-white hover:bg-[#8a3f3f] disabled:opacity-50" : "btn-primary disabled:opacity-50"}
            >
              {submitting ? (reverse ? "Stornuji…" : "Vyrábím…") : (reverse ? "Potvrdit storno" : "Potvrdit výrobu")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
